/**
 * Cart Management System - Moon Moon Agency v2.1
 * Shopify cart with AJAX updates, custom events, and UI sync
 */

/*********************************************************************************************
 * ⚙️ CONFIGURATION & CONSTANTS
 */
const CART_CONFIG = {
	// API Settings
	debounceDelay: 500,
	apiTimeout: 10000,

	// Selectors
	selectors: {
		cart: '[data-cart], .mm-cart',
		cartWrapper: '[data-cart-wrapper]',
		cartProducts: '[data-cart-products]',
		cartBubbles: '[data-cart-count-bubble]',
		addToCartForm: '[data-type="add-to-cart-form"]',
		cartClose: '[data-cart-close]',
		checkoutBtn: '[data-checkout-btn]',
		drawerCart: '[data-drawer-cart]',
		cartItemById: '[data-cart-item-id="{{id}}"]',
		lineIndex: '[data-line-index="{{index}}"]',
		itemQuantity: '[data-item-quantity]',
		freeProducts: '[data-free-products]'
	},

};

/*********************************************************************************************
 * 🛠️ UTILITIES & LOGGING
 */
// Use global debounce from mm_utils.js

// Cart Event Emitter
const cartEmit = (event, detail = {}, cancelable = false) => {
	const eventData = {
		timestamp: window.getTimestamp?.() || new Date().toLocaleString(),
		event,
		data: detail,
		source: 'cart'
	};
	
	return document.dispatchEvent(new CustomEvent(`cart:${event}`, {
		detail: eventData,
		cancelable,
		bubbles: true
	}));
};

// DOM Selectors Cache
const DOM = {
	get cart() { return document.querySelector(CART_CONFIG.selectors.cart); },
	get cartSection() { return document.getElementById(this.cart?.id || ''); },
	get cartProducts() { return this.cartSection?.querySelector(CART_CONFIG.selectors.cartProducts); },
	get cartWrapper() { return document.querySelector(CART_CONFIG.selectors.cartWrapper); },
	get cartBubbles() { return document.querySelectorAll(CART_CONFIG.selectors.cartBubbles); },
	
	cartItem: (index) => document.querySelector(CART_CONFIG.selectors.lineIndex.replace('{{index}}', index)),
	
	cartQuantity: (index) => DOM.cartItem(index)?.querySelector(CART_CONFIG.selectors.itemQuantity)
};

/*********************************************************************************************
 * 🔌 CORE CART API
 */
const CartAPI = {
	async request(method, path, body = null) {
		cartEmit('api-start', { method, path });

		const baseUrl = window.Shopify?.routes?.root || '/';
		const url = baseUrl + path;

		const isFormData = (typeof FormData !== 'undefined') && body instanceof FormData;

		const config = { method };

		// 👉 IMPORTANT : n'ajoute pas Content-Type si c'est du FormData
		if (!isFormData) {
			config.headers = { 'Content-Type': 'application/json' };
		}

		if (body && method !== 'GET') {
			config.body = isFormData ? body : JSON.stringify(body);
		}

		try {
			const response = await fetch(url, config);
			if (!response.ok) {
				throw new Error(`HTTP ${response.status}: ${response.statusText}`);
			}

			const data = await response.json();

			if (data.status && data.status >= 400) {
				throw new Error(data.message || data.description || 'Shopify API Error');
			}

			cartEmit('api-success', { method, path, itemCount: data.item_count });
			this.updateUI(data);
			return data;
		} catch (error) {
			cartEmit('api-error', { method, path, error: error.message });
			throw error;
		}
	},

	/**
	 * Updates cart UI elements with new data
	 */
	updateUI(data) {
		// Update cart count
		const count = data.item_count || 0;
		DOM.cartBubbles.forEach(bubble => {
			bubble.classList.toggle('mm-none', count === 0);
			bubble.textContent = count || '';
		});
		cartEmit('count-updated', { count });

		// Update cart content if sections exist
		if (data.sections && DOM.cart) {
			const sectionId = DOM.cart.id.replace('shopify-section-', '');
			if (data.sections[sectionId]) {
				this.refreshDOM(data.sections[sectionId]);
			}
		}
	},

	/**
	 * Refreshes cart DOM with new HTML and reinitializes components
	 */
	refreshDOM(html) {
		const temp = document.createElement('div');
		temp.innerHTML = html;
		
		const newCart = temp.querySelector(CART_CONFIG.selectors.drawerCart);
		const currentCart = DOM.cartSection?.querySelector(CART_CONFIG.selectors.drawerCart);
		
		if (newCart && currentCart) {
			currentCart.innerHTML = newCart.innerHTML;
			// Reinitialize Splide sliders with auto-init
			if (typeof Splide !== 'undefined') {
				document.querySelectorAll('.mm-drawer-cart .splide').forEach(function(element) {
					if (!element._splide) {
						try {
							new Splide(element).mount();
						} catch (error) {
							window.log('warn', 'Cart', 'Failed to initialize Splide:', { element, error });
						}
					}
				});
			}
			// Reinitialize event listeners for new DOM elements
			if (typeof window.initAddToCartForms === 'function') {
				window.initAddToCartForms();
			}
			if (typeof window.initCartQuantityListeners === 'function') {
				window.initCartQuantityListeners();
			}
			// Reinitialize promo code listeners
			if (typeof initPromoCode === 'function') {
				initPromoCode();
			}
			cartEmit('refreshed');
		}
	}
};

/*********************************************************************************************
 * 🛒 CART DATA MANAGEMENT
 */
const Cart = {
	// Focus trap state
	focusTrap: {
		isActive: false,
		triggerElement: null,
		firstFocusable: null,
		lastFocusable: null,
		handleKeyDown: null
	},

	/**
	 * Setup focus trap for cart drawer
	 */
	setupFocusTrap(cartWrapper, triggerElement) {
		const focusableElements = cartWrapper.querySelectorAll(
			'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
		);

		if (focusableElements.length === 0) return;

		this.focusTrap.triggerElement = triggerElement;
		this.focusTrap.firstFocusable = focusableElements[0];
		this.focusTrap.lastFocusable = focusableElements[focusableElements.length - 1];

		// Handle Tab and Escape keys
		this.focusTrap.handleKeyDown = (e) => {
			// Escape key closes cart
			if (e.key === 'Escape') {
				e.preventDefault();
				const trigger = this.focusTrap.triggerElement; // save before close() nullifies it
				this.close();
				trigger?.focus();
				return;
			}

			// Tab key traps focus
			if (e.key !== 'Tab') return;

			if (e.shiftKey) {
				// Shift + Tab: going backwards
				if (document.activeElement === this.focusTrap.firstFocusable) {
					e.preventDefault();
					this.focusTrap.lastFocusable?.focus();
				}
			} else {
				// Tab: going forwards
				if (document.activeElement === this.focusTrap.lastFocusable) {
					e.preventDefault();
					this.focusTrap.firstFocusable?.focus();
				}
			}
		};

		document.addEventListener('keydown', this.focusTrap.handleKeyDown);
		this.focusTrap.isActive = true;

		// Focus first element
		setTimeout(() => this.focusTrap.firstFocusable?.focus(), 100);

		window.log('info', 'Cart', 'Focus trap activated');
	},

	/**
	 * Remove focus trap
	 */
	removeFocusTrap() {
		if (this.focusTrap.isActive && this.focusTrap.handleKeyDown) {
			document.removeEventListener('keydown', this.focusTrap.handleKeyDown);
			this.focusTrap.isActive = false;
			this.focusTrap.triggerElement = null;
			this.focusTrap.firstFocusable = null;
			this.focusTrap.lastFocusable = null;
			this.focusTrap.handleKeyDown = null;
			window.log('info', 'Cart', 'Focus trap removed');
		}
	},

	async get() {
		cartEmit('fetch-start');
		const data = await CartAPI.request('GET', 'cart.js');
		cartEmit('fetched', { itemCount: data.item_count, totalPrice: data.total_price });
		return data;
	},

	/**
	 * Open cart
	 */
	open(triggerElement) {
		if (!cartEmit('before-open', {}, true)) return;

		this.get()
			/* Rattrape les papiers cadeaux dont le lien _for_item est cassé : invisibles
			   dans le drawer mais facturés au checkout (ticket 86car6xnc) */
			.then(() => window.mmSweepOrphanGiftWraps())
			.catch(error => window.log('error', 'Cart', 'Failed to fetch cart on open', { error: error.message }));

		const wrapper = DOM.cartWrapper;
		const body = document.body;

		if (wrapper && body) {
			wrapper.classList.add('mm-dc-is-open');

			// Scroll lock: position:fixed on body preserves scroll position and works on iOS Safari
			this._scrollY = window.scrollY;
			body.style.position = 'fixed';
			body.style.top = `-${this._scrollY}px`;
			body.style.width = '100%';
			body.style.overflowY = 'scroll';

			// Stop Lenis smooth scrolling (desktop)
			window.LenisManager?.stop();

			// Inert background content for screen readers (WCAG 2.4.3)
			document.querySelectorAll('main, header, footer').forEach(el => {
				if (!wrapper.contains(el)) el.inert = true;
			});

			// Hide overlays
			['shopify-chat', 'axeptio_overlay'].forEach(id => {
				const elem = document.getElementById(id);
				if (elem) elem.style.display = 'none';
			});

			// Fallback to active element so focus is always restored on close (WCAG 2.4.3)
			const trigger = triggerElement || document.activeElement;
			this.setupFocusTrap(wrapper, trigger);

			cartEmit('opened');
		}
	},

	/**
	 * Close cart
	 */
	close() {
		if (!cartEmit('before-close', {}, true)) return;

		const wrapper = DOM.cartWrapper;
		const body = document.body;

		if (wrapper) wrapper.classList.remove('mm-dc-is-open');

		// Restore scroll position
		body.style.position = '';
		body.style.top = '';
		body.style.width = '';
		body.style.overflowY = '';
		window.scrollTo({ top: this._scrollY || 0, behavior: 'instant' });
		this._scrollY = 0;

		// Resume Lenis smooth scrolling (desktop)
		window.LenisManager?.resume();
		// Recalculate Lenis scroll dimensions in case it initialized while cart was open (body was fixed)
		requestAnimationFrame(() => window.LenisManager?.resize());

		// Restore background content
		document.querySelectorAll('[inert]').forEach(el => el.inert = false);

		// Remove focus trap
		this.removeFocusTrap();

		// Show overlays
		['shopify-chat', 'axeptio_overlay'].forEach(id => {
			const elem = document.getElementById(id);
			if (elem) elem.style.display = '';
		});

		// Clean up ?open_cart=true from URL if present
		const url = new URL(window.location.href);
		if (url.searchParams.has('open_cart')) {
			url.searchParams.delete('open_cart');
			history.replaceState(null, '', url.pathname + (url.search || '') + url.hash);
		}

		cartEmit('closed');
	},

	/**
	 * Get quantity
	 */
	getQuantity(lineIndex) {
		const elem = DOM.cartQuantity(lineIndex);
		return elem ? parseInt(elem.dataset.itemQuantity, 10) || 0 : 0;
	},

	/**
	 * Extract comprehensive item data from cart DOM
	 */
	getItemData(lineIndex) {
		const itemEl = DOM.cartItem(lineIndex);
		if (!itemEl) return null;

		const data = {
			lineIndex: parseInt(lineIndex, 10),
			variantId: itemEl.dataset.cartItemId,
			itemKey: itemEl.dataset.cartItemKey,
			quantity: this.getQuantity(lineIndex),
			maxQuantity: itemEl.dataset.maxQuantity,
			properties: {},
			variants: {},
			metadata: {}
		};

		// Extract properties from DOM elements (if displayed)
		const propertyElements = itemEl.querySelectorAll('[data-property]');
		propertyElements.forEach(el => {
			const propName = el.dataset.property;
			const propValue = el.textContent.trim();
			if (propName && propValue) {
				data.properties[propName] = propValue;
			}
		});

		// Extract variant options (if displayed)
		const variantElements = itemEl.querySelectorAll('[data-variant-option]');
		variantElements.forEach(el => {
			const optionName = el.dataset.variantOption;
			const optionValue = el.textContent.trim();
			if (optionName && optionValue) {
				data.variants[optionName] = optionValue;
			}
		});

		// Add any other metadata from data attributes
		Object.keys(itemEl.dataset).forEach(key => {
			if (!['cartItemId', 'cartItemKey', 'lineIndex', 'maxQuantity'].includes(key)) {
				data.metadata[key] = itemEl.dataset[key];
			}
		});

		return data;
	},

	/**
	 * Set quantity
	 */
	setQuantity(lineIndex, newQty, skipEvent = false) {
		const elem = DOM.cartQuantity(lineIndex);
		if (!elem || newQty < 0) return;
		
		const oldQty = parseInt(elem.dataset.itemQuantity, 10) || 0;
		elem.dataset.itemQuantity = newQty;
		elem.textContent = newQty;
		
		if (!skipEvent) {
			cartEmit('quantity-changed', { lineIndex, oldQty, newQty });
		}
	},

	/**
	 * Update quantity
	 */
	async updateQuantity(lineIndex, newQty, knownKey = null, knownVariantId = null) {
		/* Clé/variant capturés au clic (via debouncedUpdate) — fiables même si le drawer
		   a été refreshé depuis, ce qui décalerait DOM.cartItem(lineIndex). Repli sur le
		   DOM si non fournis (appels legacy). */
		const itemEl = DOM.cartItem(lineIndex);
		const variantId = knownVariantId || itemEl?.dataset.cartItemId;
		const itemKey = knownKey || itemEl?.dataset.cartItemKey;

		cartEmit('quantity-sync-start', { lineIndex, quantity: newQty, variantId, itemKey });

		try {
			const sectionId = DOM.cart?.id.replace('shopify-section-', '') || '';

			/* Ciblage par clé de ligne (`id`) et non par `line` positionnel : quand
			   plusieurs lignes sont modifiées coup sur coup, chaque suppression décale
			   les index — un `line` figé pointerait alors la mauvaise ligne (voire une
			   déjà partie), laissant des articles impossibles à retirer. La clé reste
			   valable pour toute la série (ticket 86cav29fw). Repli sur `line` si la clé
			   manque (élément absent du DOM après un refresh). */
			const requestBody = itemKey
				? { id: itemKey, quantity: newQty }
				: { line: parseInt(lineIndex, 10), quantity: newQty };

			// Add sections for bundled section rendering if available
			if (sectionId) {
				requestBody.sections = sectionId;
			}

			const data = await CartAPI.request('POST', 'cart/change.js', requestBody);

			await this.processFreeProducts(data);

			/* Ligne parente supprimée → son papier cadeau n'a plus de parent : il devient
			   invisible (filtré du drawer) mais reste facturé. Le sweep le retire. */
			if (parseInt(newQty, 10) === 0) {
				await window.mmSweepOrphanGiftWraps();
			} else if (itemKey) {
				/* Un seul papier cadeau par ligne, peu importe la quantité de l'article :
				   si le gift wrap a dérivé, le ramener à 1 (jamais synchronisé sur newQty) */
				const giftItem = data.items?.find(item => item.properties?._for_item === itemKey);
				if (giftItem && giftItem.quantity !== 1) {
					const giftLineIdx = data.items.indexOf(giftItem);
					const giftBody = { line: giftLineIdx + 1, quantity: 1 };
					if (sectionId) giftBody.sections = sectionId;
					await CartAPI.request('POST', 'cart/change.js', giftBody);
				}
			}

			cartEmit('quantity-synced', { lineIndex, quantity: newQty, variantId, itemKey });
			return data;
		} catch (error) {
			cartEmit('quantity-sync-error', { lineIndex, error: error.message });
			// Fallback: refresh cart to sync with server state
			this.get().catch(error => window.log('error', 'Cart', 'Failed to fetch cart on open', { error: error.message }));
			throw error;
		}
	},

	/**
	 * Process free products
	 */
	async processFreeProducts(data) {
		const sectionId = DOM.cart?.id.replace('shopify-section-', '') || '';
		if (!data.sections?.[sectionId]) return data;

		const temp = document.createElement('div');
		temp.innerHTML = data.sections[sectionId];
		const freeProductsEl = temp.querySelector(CART_CONFIG.selectors.freeProducts);
		
		if (!freeProductsEl?.children.length) return data;

		const updates = {};
		Array.from(freeProductsEl.children).forEach(el => {
			const variantId = el.dataset.variantId;
			const shouldHave = el.dataset.freeProduct === 'true';
			const hasIt = !!document.querySelector(CART_CONFIG.selectors.cartItemById.replace('{{id}}', variantId));

			if (hasIt && !shouldHave) {
				updates[variantId] = 0;
				cartEmit('free-product-removing', { variantId });
			} else if (!hasIt && shouldHave) {
				updates[variantId] = 1;
				cartEmit('free-product-adding', { variantId });
			}
		});

		if (Object.keys(updates).length > 0) {
			try {
				// Shopify cart/update.js expects updates object and optional sections
				const requestBody = { updates };
				
				if (sectionId) {
					requestBody.sections = sectionId;
				}
				
				await CartAPI.request('POST', 'cart/update.js', requestBody);
				cartEmit('free-products-updated', { updates });
			} catch (error) {
				cartEmit('free-products-error', { error: error.message });
			}
		}

		return data;
	}
};

/*********************************************************************************************
 * 🔢 QUANTITY CONTROLS
 */
/* Debounce PAR LIGNE + file d'attente sérialisée (ticket 86cav29fw).

   Deux raisons de ne pas garder un debounce global unique :
   1. Coalescing inter-lignes : un `window.debounce` partagé n'a qu'un seul timer.
      Cliquer « − » sur la ligne 1 puis la ligne 2 dans la même fenêtre annulait le
      premier appel — seule la dernière opération partait, laissant un article
      impossible à supprimer. Chaque ligne a donc son propre timer, indexé par clé.
   2. Sérialisation : deux `cart/change.js` concurrents se marchent dessus (les index
      se décalent entre les deux). Les updates sont enchaînés sur une seule promesse.

   La quantité cible est passée explicitement, jamais relue depuis le DOM au moment du
   debounce : un refresh concurrent du drawer re-render le SSR avec l'ancienne quantité,
   et un getQuantity différé y relisait « 1 » au lieu de « 0 ». */
const _pendingUpdates = new Map(); // clé de coalescing → timeout id
let _updateChain = Promise.resolve();

const debouncedUpdate = (lineIndex, quantity) => {
	/* Clé/variant capturés MAINTENANT (au clic), pas à l'exécution différée : le drawer
	   peut être refreshé entre-temps et décaler DOM.cartItem(lineIndex). */
	const itemEl = DOM.cartItem(lineIndex);
	const itemKey = itemEl?.dataset.cartItemKey || null;
	const variantId = itemEl?.dataset.cartItemId || null;
	// Clé stable pour le coalescing : la clé de ligne si dispo, sinon l'index.
	const coalesceKey = itemKey || `line-${lineIndex}`;

	clearTimeout(_pendingUpdates.get(coalesceKey));
	_pendingUpdates.set(coalesceKey, setTimeout(() => {
		_pendingUpdates.delete(coalesceKey);
		// Enchaîne sur la file : jamais deux cart/change.js en vol simultanément.
		_updateChain = _updateChain
			.catch(() => {})
			.then(() => Cart.updateQuantity(lineIndex, quantity, itemKey, variantId));
	}, CART_CONFIG.debounceDelay));
};

/*********************************************************************************************
 * 🛏️ EXPORTED FUNCTIONS
 */

/**
 * Increases product quantity by 1
 */
window.mmIncreaseProductQuantity = (lineIndex) => {
	const itemData = Cart.getItemData(lineIndex);
	const current = Cart.getQuantity(lineIndex);
	const maxValue = itemData?.maxQuantity;
	const max = (maxValue && !isNaN(parseInt(maxValue, 10))) ? parseInt(maxValue, 10) : Infinity;
	const newQty = current + 1;

	const eventData = {
		...itemData,
		oldQty: current,
		newQty,
		max,
		action: 'increase'
	};

	if (!cartEmit('before-quantity-increase', eventData, true)) return;
	
	if (newQty > max) {
		cartEmit('quantity-limit-reached', eventData);
		return;
	}

	Cart.setQuantity(lineIndex, newQty);
	cartEmit('quantity-increased', { ...eventData, newQty });
	debouncedUpdate(lineIndex, newQty);
};

/**
 * Decreases product quantity by 1, removes if quantity would be 0
 */
window.mmDecreaseProductQuantity = (lineIndex) => {
	const itemData = Cart.getItemData(lineIndex);
	const current = Cart.getQuantity(lineIndex);
	const newQty = current - 1;

	const eventData = {
		...itemData,
		oldQty: current,
		newQty,
		action: 'decrease'
	};

	if (!cartEmit('before-quantity-decrease', eventData, true)) return;
	
	if (newQty < 1) {
		// If quantity would be less than 1, remove the item completely
		const removeEventData = {
			...itemData,
			oldQty: current,
			action: 'remove'
		};
		
		if (!cartEmit('before-item-remove', removeEventData, true)) return;
		
		Cart.setQuantity(lineIndex, 0);
		cartEmit('item-removed', removeEventData);
		debouncedUpdate(lineIndex, 0);
		return;
	}

	Cart.setQuantity(lineIndex, newQty);
	cartEmit('quantity-decreased', eventData);
	debouncedUpdate(lineIndex, newQty);
};

/**
 * Removes product completely from cart
 */
window.mmDestroyProductFromCart = (lineIndex) => {
	const itemData = Cart.getItemData(lineIndex);
	const current = Cart.getQuantity(lineIndex);

	const eventData = {
		...itemData,
		oldQty: current,
		action: 'destroy'
	};

	if (!cartEmit('before-item-remove', eventData, true)) return;

	Cart.setQuantity(lineIndex, 0);
	cartEmit('item-removed', eventData);
	debouncedUpdate(lineIndex, 0);
};

/*********************************************************************************************
 * 🏗️ GLOBAL FUNCTIONS
 */
window.mmOpenCart = (triggerElement) => Cart.open(triggerElement);
window.mmCloseCart = () => Cart.close();
window.mmGetCart = () => Cart.get();
window.mmGetCartItemData = (lineIndex) => Cart.getItemData(lineIndex);

/*********************************************************************************************
 * 🏗️ UTILITY FUNCTIONS (MERGED ABOVE)
 */
window.updateCartCount = (value) => CartAPI.updateUI({ item_count: value });

// Cart UI Event Listeners
const initCartEventListeners = () => {
	// Close cart functionality
	document.addEventListener('click', (e) => {
		if (e.target.closest('[data-cart-close]')) {
			e.preventDefault();
			Cart.close();
		}

		// Checkout button loading
		if (e.target.closest('[data-checkout-btn]')) {
			const btn = e.target.closest('[data-checkout-btn]');
			btn.classList.add('mm-loading');
			cartEmit('checkout-loading', { element: btn });
		}

		// Discount removal
		const discountButton = e.target.closest('[data-discount-remove]');
		if (discountButton) {
			e.preventDefault();
			const discountTitle = discountButton.getAttribute('data-discount-title');
			if (discountTitle) {
				window.removeDiscount(discountTitle);
			}
		}
	});

	/* Retour depuis le checkout via bfcache : la page est restaurée telle quelle.
	   1. Le CTA garde la classe mm-loading → spinner infini. On la nettoie.
	   2. Le badge compteur panier reflète l'état figé du snapshot (souvent vide alors
	      que le panier contient un produit). On re-synchronise via cart.js. */
	window.addEventListener('pageshow', (e) => {
		if (!e.persisted) return;
		document.querySelectorAll('[data-checkout-btn].mm-loading').forEach((btn) => {
			btn.classList.remove('mm-loading');
		});
		CartAPI.request('GET', 'cart.js')
			.then((data) => CartAPI.updateUI(data))
			.catch((error) => window.log('warn', 'Cart', 'Failed to resync cart count on bfcache restore', { error: error.message }));
	});
};

/*********************************************************************************************
 * 🛒 Add to Cart Forms Handler
 */
window.initAddToCartForms = () => {
	document.querySelectorAll('[data-type="add-to-cart-form"]:not([data-cart-handler-attached])').forEach(form => {
		form.setAttribute('data-cart-handler-attached', 'true');
		form.addEventListener('submit', async (e) => {
			e.preventDefault();

			const submitButton = form.querySelector('[type="submit"]');
			const formData = new FormData(form);

			if (!formData.get('id')) {
				window.log('error', 'Cart', 'No variant ID found in form', { form });
				return;
			}

			// Add loading state
			if (submitButton) {
				submitButton.classList.add('mm-loading');
				submitButton.disabled = true;
			}

			// Extract all form data including properties and options
			const extractedData = {
				variantId: formData.get('id'),
				quantity: parseInt(formData.get('quantity') || 1, 10),
				properties: {},
				options: {},
				formEntries: {}
			};

			// Collect all form entries
			for (const [key, value] of formData.entries()) {
				extractedData.formEntries[key] = value;

				// Extract properties (custom fields)
				if (key.startsWith('properties[')) {
					const propName = key.match(/properties\[(.+)\]/)?.[1];
					if (propName && value) {
						extractedData.properties[propName] = value;
					}
				}

				// Extract options (variant selections like size, color)
				if (key.startsWith('option')) {
					extractedData.options[key] = value;
				}
			}

			cartEmit('add-to-cart-start', extractedData);

			try {
				const sectionId = DOM.cart?.id.replace('shopify-section-', '') || '';

				// Add sections for cart update if available
				if (sectionId) {
					formData.append('sections', sectionId);
				}

				const response = await CartAPI.request('POST', 'cart/add.js', formData);
				cartEmit('add-to-cart-success', {
					...extractedData,
					response: response
				});

				// Process free products after adding to cart
				await Cart.processFreeProducts(response);

				// Auto-open cart after successful add
				Cart.open();

			} catch (error) {
				cartEmit('add-to-cart-error', {
					...extractedData,
					error: error.message
				});
				window.log('error', 'Cart', 'Add to cart failed', {
					error: error.message,
					...extractedData
				});
			} finally {
				// Remove loading state
				if (submitButton) {
					submitButton.classList.remove('mm-loading');
					submitButton.disabled = false;
				}
			}
		});
	});
};

/*********************************************************************************************
 * 🎯 ACCESSIBILITY - Quantity Selector Event Listeners (replace inline onclick)
 */
window.initCartQuantityListeners = function initCartQuantityListeners() {
	// Cart decrease buttons
	document.querySelectorAll('[data-cart-decrease]').forEach((btn) => {
		btn.addEventListener('click', () => {
			const lineIndex = btn.dataset.cartDecrease;
			const variantId = btn.dataset.variantId;
			const priceContainer = document.querySelector(`.mm-dc-product-price-container-${variantId}`);

			window.mmDecreaseProductQuantity(lineIndex);
			if (priceContainer) window.LoaderManager.loadThis(priceContainer);
		});
	});

	// Cart increase buttons
	document.querySelectorAll('[data-cart-increase]').forEach((btn) => {
		btn.addEventListener('click', () => {
			const lineIndex = btn.dataset.cartIncrease;
			const variantId = btn.dataset.variantId;
			const priceContainer = document.querySelector(`.mm-dc-product-price-container-${variantId}`);

			window.mmIncreaseProductQuantity(lineIndex);
			if (priceContainer) window.LoaderManager.loadThis(priceContainer);
		});
	});

	// Cart remove buttons (delete item)
	document.querySelectorAll('[data-cart-remove]').forEach((btn) => {
		btn.addEventListener('click', () => {
			const lineIndex = btn.dataset.cartRemove;
			const variantId = btn.dataset.variantId;
			const priceContainer = document.querySelector(`.mm-dc-product-price-container-${variantId}`);

			window.mmDestroyProductFromCart(lineIndex);
			if (priceContainer) {
				window.LoaderManager.loadThis(priceContainer);
			} else {
				// For free products, load the button itself
				window.LoaderManager.loadThis(btn);
			}
		});
	});

	// Gift wrap checkboxes
	document.querySelectorAll('[data-gift-wrap-toggle]:not([data-gift-wrap-handler-attached])').forEach((checkbox) => {
		checkbox.dataset.giftWrapHandlerAttached = 'true';
		checkbox.addEventListener('change', () => {
			window.mmToggleGiftWrap(checkbox);
		});
	});
}

/*********************************************************************************************
 * 🎁 GIFT WRAP — SWEEP ORPHELINS
 *
 * Un papier cadeau est lié à sa ligne parente par la property `_for_item` (= clé Shopify
 * du parent). Cette clé change à chaque écriture de properties sur le parent, et la ligne
 * gift wrap est filtrée du drawer (MM-Cart.liquid) : un lien cassé donne donc une ligne
 * invisible, non supprimable à la main, mais facturée au checkout (ticket 86car6xnc).
 *
 * Est orphelin un gift wrap dont le `_for_item` est présent mais ne résout plus. Un gift
 * wrap sans `_for_item` a été acheté comme produit normal : il est laissé intact.
 * Le sweep tourne à l'ouverture du drawer et rattrape aussi les paniers déjà pollués.
 */
window.mmSweepOrphanGiftWraps = async function({ force = false } = {}) {
	/* L'id est porté par le drawer, pas par la <section> que résout DOM.cart */
	const drawer = document.querySelector(CART_CONFIG.selectors.drawerCart);
	const giftWrapVariantId = parseInt(drawer?.dataset.cartGiftWrapId, 10);
	if (!giftWrapVariantId) return;

	const sectionId = DOM.cart?.id.replace('shopify-section-', '') || '';
	const baseUrl = window.Shopify?.routes?.root || '/';

	try {
		const res = await fetch(baseUrl + 'cart.js');
		if (!res.ok) throw new Error(`HTTP ${res.status}`);
		const cartData = await res.json();

		const liveKeys = new Set(
			cartData.items.filter(item => item.variant_id !== giftWrapVariantId).map(item => item.key)
		);
		/* Orphelin = gift wrap AYANT un `_for_item` qui ne résout plus.
		   Un papier cadeau sans `_for_item` a été acheté comme produit normal (PDP,
		   collection) : il n'appartient pas à la feature, on n'y touche pas. */
		const orphans = cartData.items.filter(
			item => item.variant_id === giftWrapVariantId
				&& item.properties?._for_item
				&& !liveKeys.has(item.properties._for_item)
		);
		if (!orphans.length) {
			/* Le retrait a échoué sans orphelin détecté : rafraîchir pour resynchroniser
			   le DOM. `sections` est obligatoire, sinon updateUI ne repeint rien. */
			if (force && sectionId) {
				await CartAPI.request('GET', `cart.js?sections=${sectionId}`);
			}
			return;
		}

		/* Suppression par clé (`id` accepte la clé de ligne) : les index se décalent
		   à chaque retrait, une clé reste valable pour toute la série. */
		for (const orphan of orphans) {
			const body = { id: orphan.key, quantity: 0 };
			if (sectionId) body.sections = sectionId;
			await CartAPI.request('POST', 'cart/change.js', body);
		}

		window.log('info', 'Cart', 'Orphan gift wraps removed', { count: orphans.length });
	} catch (error) {
		window.log('error', 'Cart', 'Orphan gift wrap sweep failed', { error: error.message });
	}
};

/*********************************************************************************************
 * 🎁 GIFT WRAP TOGGLE
 */
window.mmToggleGiftWrap = async function(checkbox) {
	const itemKey = checkbox.dataset.itemKey;
	const giftWrapVariantId = parseInt(checkbox.dataset.giftWrapVariantId, 10);
	const giftWrapLabel = checkbox.dataset.giftWrapLabel || 'Paquet cadeau';
	const isChecked = checkbox.checked;
	const sectionId = DOM.cart?.id.replace('shopify-section-', '') || '';

	checkbox.disabled = true;

	const baseUrl = window.Shopify?.routes?.root || '/';

	/* Raw fetch — sans side-effects CartAPI (pas de refresh du count intermédiaire) */
	const apiFetch = async (method, path, body = null) => {
		const opts = { method, headers: { 'Content-Type': 'application/json' } };
		if (body) opts.body = JSON.stringify(body);
		const res = await fetch(baseUrl + path, opts);
		if (!res.ok) throw new Error(`HTTP ${res.status}`);
		return res.json();
	};

	/* Numéro de ligne Shopify du produit parent (1-based, depuis le DOM) */
	const parentLine = parseInt(checkbox.closest('[data-line-index]')?.dataset.lineIndex, 10);

	try {
		const cartData = await apiFetch('GET', 'cart.js');
		const parentItem = cartData.items[parentLine - 1];
		const currentProps = parentItem?.properties ? { ...parentItem.properties } : {};

		/* Clé parente lue sur le panier serveur, jamais sur le dataset SSR :
		   la clé change à chaque écriture de properties, donc data-item-key peut être
		   périmé (ticket 86car6xnc — le papier cadeau restait en panier et était facturé) */
		const parentKey = parentItem?.key || itemKey;

		if (isChecked) {
			/* 1. Met à jour la propriété checkout sur le parent, ciblé par clé (sans refresh).
			      La clé Shopify de la ligne change quand ses propriétés changent — on relit
			      la nouvelle clé sur la ligne portant le « ✓ » pour l'utiliser comme
			      _for_item (jamais par index : Shopify peut réordonner les lignes). */
			const changeResult = await apiFetch('POST', 'cart/change.js', {
				id: parentKey,
				quantity: parentItem.quantity,
				properties: { ...currentProps, [giftWrapLabel]: '✓' }
			});
			const updatedParent = changeResult.items?.find(
				item => item.variant_id === parentItem.variant_id && item.properties?.[giftWrapLabel] === '✓'
			);
			const updatedItemKey = updatedParent?.key || itemKey;

			/* 2. Ajoute le gift wrap avec sections → déclenche le refresh
			      Toujours 1 seule unité de papier cadeau par ligne, peu importe la quantité de l'article
			      À ce moment gift_wrap_active = true, la checkbox s'affiche cochée */
			const body = { id: giftWrapVariantId, quantity: 1, properties: { _for_item: updatedItemKey } };
			if (sectionId) body.sections = sectionId;
			await CartAPI.request('POST', 'cart/add.js', body);

		} else {
			/* Résolution sur la clé serveur, avec repli sur la clé SSR si le panier a bougé.
			   Le repli couvre le cas où le parent a été réécrit sans re-render du drawer. */
			const giftLine = cartData.items.find(
				item => item.variant_id === giftWrapVariantId
					&& (item.properties?._for_item === parentKey || item.properties?._for_item === itemKey)
			);

			/* 1. Supprime le gift wrap AVANT de toucher au parent : réécrire les properties
			      du parent change sa clé et casserait le lien _for_item en cours de route.
			      Suppression par clé (jamais par index : il se décale). */
			if (giftLine) {
				await apiFetch('POST', 'cart/change.js', { id: giftLine.key, quantity: 0 });
			}

			/* 2. Retire la propriété checkout du parent, avec refresh du drawer.
			      Ciblage par clé et non par `line` : la suppression du gift wrap ci-dessus
			      a décalé les index, et `parentLine` (rendu avant) pointerait à côté — la
			      property « ✓ » restait alors sur le parent et la case revenait cochée.
			      Shopify ignore un objet properties vide (« It's not possible to set a line
			      item's properties to an empty object once a value is set ») : on neutralise
			      donc la clé par une chaîne vide plutôt que par delete, sinon le « ✓ »
			      restait collé au parent et ressortait dans le mail de confirmation. */
			const newProps = { ...currentProps };
			if (Object.keys(newProps).length === 1 && giftWrapLabel in newProps) {
				newProps[giftWrapLabel] = '';
			} else {
				delete newProps[giftWrapLabel];
			}
			const parentBody = { id: parentKey, quantity: parentItem.quantity, properties: newProps };
			if (sectionId) parentBody.sections = sectionId;
			try {
				await CartAPI.request('POST', 'cart/change.js', parentBody);
			} catch (error) {
				/* Le « ✓ » est resté sur le parent alors que la ligne facturée vient de
				   sauter : la commande partirait avec un emballage offert. On remet le
				   papier cadeau pour garder panier et mention cohérents. */
				if (giftLine) {
					await apiFetch('POST', 'cart/add.js', {
						id: giftWrapVariantId,
						quantity: 1,
						properties: { _for_item: parentKey }
					});
				}
				throw error;
			}

			/* 3. Lien _for_item cassé : ne pas sortir silencieusement (ça laissait un
			      papier cadeau facturé et invisible dans le drawer) — le sweep rattrape. */
			if (!giftLine) {
				window.log('warn', 'Cart', 'Gift wrap line not found, falling back to orphan sweep', { parentKey });
				await window.mmSweepOrphanGiftWraps({ force: true });
			}
		}
	} catch (error) {
		checkbox.checked = !isChecked;
		window.log('error', 'Cart', 'Gift wrap toggle failed', { error: error.message });
	} finally {
		checkbox.disabled = false;
	}
};


/*********************************************************************************************
 * 💰 PROMO CODE MANAGEMENT
 */
function initPromoCode() {
	const applyBtn = document.querySelector('[data-action="apply-discount"]');
	const discountInput = document.querySelector('input[data-name="discount"]');

	window.log('info', 'Cart', 'Initializing promo code', { applyBtn: !!applyBtn, discountInput: !!discountInput });

	if (applyBtn && discountInput && !applyBtn.dataset.promoHandlerAttached) {
		applyBtn.dataset.promoHandlerAttached = 'true';
		applyBtn.addEventListener('click', (e) => {
			e.preventDefault();
			e.stopPropagation();
			window.log('info', 'Cart', 'Apply discount button clicked');
			applyDiscount();
		});
		discountInput.addEventListener('keypress', (e) => {
			if (e.key === 'Enter') {
				e.preventDefault();
				e.stopPropagation();
				window.log('info', 'Cart', 'Enter key pressed in discount input');
				applyDiscount();
			}
		});
		// Hide error when user starts typing
		discountInput.addEventListener('input', () => {
			hidePromoError();
		});
		window.log('info', 'Cart', 'Promo code listeners attached successfully');
	}
}

async function applyDiscount() {
	window.log('info', 'Cart', 'applyDiscount function called');

	const input = document.querySelector('input[data-name="discount"]');
	const button = document.querySelector('[data-action="apply-discount"]');

	if (!input || !button) {
		window.log('error', 'Cart', 'Input or button not found', { input: !!input, button: !!button });
		return;
	}

	const code = input.value.trim();
	window.log('info', 'Cart', 'Discount code entered', { code });

	if (!code) {
		window.log('warn', 'Cart', 'Empty discount code');
		return;
	}

	// Disable button and show loading state
	button.disabled = true;
	button.classList.add('mm-loading');

	try {
		// Get cart state before applying discount
		window.log('info', 'Cart', 'Getting cart state before discount...');
		const cartBefore = await CartAPI.request('GET', 'cart.js');
		const discountsBefore = cartBefore.cart_level_discount_applications || [];
		window.log('info', 'Cart', 'Cart before', {
			total: cartBefore.total_price,
			discounts: discountsBefore.length,
			discountsList: discountsBefore
		});

		// Check if this exact code is already applied
		const codeAlreadyApplied = discountsBefore.some(d => {
			const discountCode = d.title || d.code;
			return discountCode && discountCode.toLowerCase() === code.toLowerCase();
		});

		if (codeAlreadyApplied) {
			showPromoError(input.dataset.errorAlreadyApplied || 'Code already applied');
			window.log('info', 'Cart', 'This code is already applied', { code });
			input.value = ''; // Clear invalid code
			return;
		}

		// Check if another discount is already applied (Shopify allows only one)
		if (discountsBefore.length > 0) {
			const existingDiscount = discountsBefore[0].title || discountsBefore[0].code;
			showPromoError(`${input.dataset.errorAlreadyApplied || 'Code already applied'} (${existingDiscount})`);
			window.log('info', 'Cart', 'Another discount already applied', { existing: existingDiscount, attempted: code });
			input.value = ''; // Clear invalid code
			return;
		}

		// Check if cart is empty
		if (cartBefore.item_count === 0) {
			showPromoError(input.dataset.errorEmptyCart || 'Cart is empty');
			window.log('info', 'Cart', 'Cart is empty');
			input.value = ''; // Clear invalid code
			return;
		}

		// Apply discount via cart/update.js API
		window.log('info', 'Cart', 'Applying discount via API...', { code });

		// Get section ID for cart refresh
		const sectionId = DOM.cart?.id.replace('shopify-section-', '') || '';

		const updatePayload = {
			discount: code
		};

		if (sectionId) {
			updatePayload.sections = sectionId;
		}

		const response = await fetch(window.Shopify.routes.root + 'cart/update.js', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(updatePayload)
		});

		if (!response.ok) {
			throw new Error('Cart update failed');
		}

		const updateData = await response.json();
		window.log('info', 'Cart', 'Cart updated', { updateData });

		// Verify if discount was applied
		const cartAfter = await CartAPI.request('GET', 'cart.js');
		const codeApplied = cartAfter.cart_level_discount_applications?.some(d => {
			const discountTitle = d.title || d.code;
			return discountTitle && discountTitle.toLowerCase() === code.toLowerCase();
		});

		if (!codeApplied) {
			// Discount was not applied - code is invalid
			window.log('warn', 'Cart', 'Discount was not applied by Shopify', { code });

			// IMPORTANT: Force removal of invalid code from cart
			const cleanupPayload = {
				discount: '' // Empty string removes the discount
			};

			if (sectionId) {
				cleanupPayload.sections = sectionId;
			}

			try {
				await fetch(window.Shopify.routes.root + 'cart/update.js', {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify(cleanupPayload)
				});
				window.log('info', 'Cart', 'Invalid code cleaned from cart', { code });
			} catch (cleanupError) {
				window.log('error', 'Cart', 'Failed to clean invalid code', { error: cleanupError.message });
			}

			showPromoError(input.dataset.errorNotApplicable || 'Discount code not applicable');
			return;
		}

		// Discount applied successfully, refresh UI
		window.log('info', 'Cart', 'Discount applied successfully');

		if (updateData.sections && updateData.sections[sectionId]) {
			CartAPI.refreshDOM(updateData.sections[sectionId]);
		} else {
			// Fallback: manual refresh
			if (DOM.cart) {
				const cartData = await CartAPI.request('GET', `cart.js?sections=${sectionId}`);
				if (cartData.sections && cartData.sections[sectionId]) {
					CartAPI.refreshDOM(cartData.sections[sectionId]);
				}
			}
		}

		// Clear input
		input.value = '';

		// Emit success event
		cartEmit('discount-applied', { code });
		window.log('info', 'Cart', 'Discount applied and cart refreshed');

	} catch (error) {
		window.log('error', 'Cart', 'Failed to apply discount', { error: error.message, stack: error.stack });
		showPromoError(input.dataset.errorText || 'Error applying discount code');
	} finally {
		// Reset button state
		button.disabled = false;
		button.classList.remove('mm-loading');
	}
}

function showPromoError(message) {
	const errorElement = document.querySelector('[data-promo-error]');
	const input = document.querySelector('input[data-name="discount"]');

	if (errorElement) {
		const errorText = errorElement.querySelector('.mm-dc-promo-error-text');
		if (errorText) errorText.textContent = message;
		errorElement.classList.remove('mm-none');
		errorElement.classList.add('mm-flex');
	}

	if (input) {
		input.classList.add('error');
	}

	// Auto-hide after 5 seconds
	setTimeout(() => {
		hidePromoError();
	}, 5000);
}

function hidePromoError() {
	const errorElement = document.querySelector('[data-promo-error]');
	const input = document.querySelector('input[data-name="discount"]');

	if (errorElement) {
		errorElement.classList.add('mm-none');
		errorElement.classList.remove('mm-flex');
		const errorText = errorElement.querySelector('.mm-dc-promo-error-text');
		if (errorText) errorText.textContent = '';
	}

	if (input) {
		input.classList.remove('error');
	}
}

window.removeDiscount = async function(code) {
	window.log('info', 'Cart', 'Removing discount', { code });

	const discountButton = document.querySelector(`[data-discount-code="${code.toLowerCase()}"] [data-discount-remove]`);

	if (discountButton) {
		// Add loading state
		discountButton.disabled = true;
		discountButton.style.opacity = '0.5';
	}

	try {
		// Get section ID for cart refresh
		const sectionId = DOM.cart?.id.replace('shopify-section-', '') || '';

		// Remove discount via cart/update.js API with empty discount parameter
		window.log('info', 'Cart', 'Removing discount via API...', { code });

		const updatePayload = {
			discount: ''  // Empty string removes the discount
		};

		if (sectionId) {
			updatePayload.sections = sectionId;
		}

		const response = await fetch(window.Shopify.routes.root + 'cart/update.js', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(updatePayload)
		});

		if (!response.ok) {
			throw new Error('Cart update failed');
		}

		const updateData = await response.json();
		window.log('info', 'Cart', 'Discount removal response', { updateData });

		// Verify discount was removed
		const cartAfter = await CartAPI.request('GET', 'cart.js');
		const discountStillPresent = cartAfter.cart_level_discount_applications?.some(d => {
			const discountTitle = d.title || d.code;
			return discountTitle && discountTitle.toLowerCase() === code.toLowerCase();
		});

		if (discountStillPresent) {
			window.log('error', 'Cart', 'Discount was not removed', { code });
			throw new Error('Discount removal failed');
		}

		window.log('info', 'Cart', 'Discount removed successfully', { code });

		// Refresh UI
		if (updateData.sections && updateData.sections[sectionId]) {
			CartAPI.refreshDOM(updateData.sections[sectionId]);
		} else {
			// Fallback: manual refresh
			if (DOM.cart) {
				const cartData = await CartAPI.request('GET', `cart.js?sections=${sectionId}`);
				if (cartData.sections && cartData.sections[sectionId]) {
					CartAPI.refreshDOM(cartData.sections[sectionId]);
				}
			}
		}

		// Emit success event
		cartEmit('discount-removed', { code });
		window.log('info', 'Cart', 'Discount removed and cart refreshed');

	} catch (error) {
		window.log('error', 'Cart', 'Failed to remove discount', { error: error.message, stack: error.stack });

		// Reset button state on error
		if (discountButton) {
			discountButton.disabled = false;
			discountButton.style.opacity = '1';
		}
	}
};

/*********************************************************************************************
 * 🚀 INITIALIZATION
 */
window.mmCartUpdateUI = (data) => CartAPI.updateUI(data);
window.mmCartSectionId = () => DOM.cart?.id.replace('shopify-section-', '') || '';

/*********************************************************************************************
 * 🚚 SHIPPING ESTIMATE MANAGER
 * Frais de port saisis dans la devise boutique (ex. « 25.28 EUR », marché en devises locales) :
 * conversion dans la devise du client au taux Shopify, puis ajout au total du panier.
 * MM-Cart.liquid ne pose les data-attributes que lorsqu'une conversion est nécessaire.
 */
const ShippingEstimateManager = {
	config: {
		selectors: {
			shipping: '[data-mm-shipping-convert]',
			total: '[data-mm-total-convert]',
		},
	},

	init() {
		this.convert();
		document.addEventListener('cart:refreshed', () => this.convert());
	},

	/**
	 * Convertit les frais (centimes devise boutique) et complète le total affiché
	 */
	convert() {
		const shippingEl = window.getElement(this.config.selectors.shipping);
		const rate = parseFloat(window.Shopify?.currency?.rate);
		if (!shippingEl || !rate) return;

		const shippingCents = Math.round(Number(shippingEl.dataset.mmShippingConvert) * rate);
		shippingEl.textContent = window.formatMoney(shippingCents);

		const totalEl = window.getElement(this.config.selectors.total);
		if (totalEl) {
			totalEl.textContent = window.formatMoney(Number(totalEl.dataset.mmTotalConvert) + shippingCents);
		}
		window.log('info', '🚚 [SHIPPING]', 'Estimate converted', { shippingCents, rate });
	},
};

document.addEventListener('DOMContentLoaded', () => {
	cartEmit('initializing');
	window.initCartQuantityListeners();
	initPromoCode();
	ShippingEstimateManager.init();

	Cart.get()
		.then(data => {
			cartEmit('initialized', { itemCount: data.item_count, totalPrice: data.total_price });
			initCartEventListeners();
			window.initAddToCartForms();
		})
		.catch(error => {
			window.log('error', 'Cart', 'Cart initialization failed', { error: error.message });
			cartEmit('initialization-error', { error: error.message });
		});
});

